/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
/* eslint-disable @atlaskit/design-system/use-tokens-typography */
/* eslint-disable @atlaskit/design-system/use-primitives-text */
/* eslint-disable @atlaskit/design-system/use-heading */
/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values */
/* eslint-disable @atlaskit/ui-styling-standard/no-unsafe-values */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useEffect, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';

import { useAnalyticsEvents } from '../../../../common/analytics/generated/use-analytics-events';
import { useAtlassianUserContext } from '../../../../hooks/use-user-context';
import { messages } from './messages';
import {
	HERO_TYPOGRAPHY_WORDMARK_VIEWBOX,
	type LetterIntroStyle,
	type ScribbleEaseName,
} from './typography/hero-typography-wordmark-constants';
import { HeroTypographyWordmark } from './typography/HeroTypographyWordmark';
import { useMediaQuery } from './use-media-query';
import type { HeroViewportTier } from './use-viewport-tier';

const WORDMARK_WIDE_VIEWPORT_QUERY = '(min-width: 769px)';
const WORDMARK_MAX_WIDTH = {
	base: 'min(800px, calc(100vw - 96px))',
	wide: 'min(800px, calc(100vw - 256px))',
} as const;

const CONTENT_SPECS: Record<
	HeroViewportTier,
	{
		wordmarkDescriptionGap: string;
		descriptionCtaGap: string;
		descriptionMaxWidth: string;
		descriptionFontSize: string;
		verticalOffset: string;
	}
> = {
	desktop: {
		wordmarkDescriptionGap: '40px',
		descriptionCtaGap: '40px',
		descriptionMaxWidth: '640px',
		descriptionFontSize: '32px',
		verticalOffset: '0px',
	},
	compact: {
		wordmarkDescriptionGap: '32px',
		descriptionCtaGap: '32px',
		descriptionMaxWidth: '600px',
		descriptionFontSize: '28px',
		verticalOffset: '0px',
	},
	mobile: {
		wordmarkDescriptionGap: 'clamp(18px, 3.5dvh, 24px)',
		descriptionCtaGap: 'clamp(20px, 4dvh, 28px)',
		descriptionMaxWidth: 'min(82vw, 390px)',
		descriptionFontSize: 'clamp(22px, 6vw, 26px)',
		verticalOffset: 'clamp(0px, 2dvh, 24px)',
	},
};

const styles = cssMap({
	overlay: {
		position: 'absolute',
		inset: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		pointerEvents: 'none',
		textAlign: 'center',
		zIndex: 2,
	},
	stack: {
		display: 'flex',
		width: '100%',
		flexDirection: 'column',
		alignItems: 'center',
		pointerEvents: 'none',
	},
	wordmarkFrame: {
		width: '100%',
		pointerEvents: 'none',
	},
	lowerStack: {
		display: 'flex',
		flexShrink: 0,
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'flex-end',
		pointerEvents: 'none',
	},
	subtitle: {
		pointerEvents: 'auto',
		textAlign: 'center',
		fontWeight: token('font.weight.regular'),
		letterSpacing: '-0.02em',
		color: '#C7C9CE',
		fontFamily: token('font.family.brand.heading'),
		lineHeight: 1.2,
		margin: 0,
		willChange: 'opacity',
		transform: 'translateZ(0)',
	},
	ctaWrapper: {
		display: 'inline-flex',
		pointerEvents: 'auto',
		willChange: 'opacity',
		transform: 'translateZ(0)',
	},
	ctaButton: {
		appearance: 'none',
		WebkitAppearance: 'none',
		display: 'flex',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingInline: '10.5px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBlock: '6.5px',
		alignItems: 'center',
		gap: token('space.075'),
		borderRadius: token('radius.medium'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
		borderWidth: '1.5px',
		borderStyle: 'solid',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderColor: '#505258',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#111213',
		font: token('font.body'),
		cursor: 'pointer',
		transition: token('motion.button.hovered'),
		zIndex: 1,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '20px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		fontWeight: token('font.weight.medium'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	ctaButtonMotion: {
		transition: token('motion.button.hovered'),
	},
});

function useDelayedReveal(delayMs: number, resetKey: string): boolean {
	const initiallyVisible = delayMs <= 0;
	const [visible, setVisible] = useState(initiallyVisible);

	useEffect(() => {
		if (initiallyVisible) {
			return;
		}
		setVisible(false);
		const id = window.setTimeout(() => setVisible(true), delayMs);
		return () => window.clearTimeout(id);
	}, [delayMs, initiallyVisible, resetKey]);

	return visible;
}

export type ContentStackProps = {
	mountKey: string;
	viewportTier: HeroViewportTier;
	hero: { scale: number };
	letterA: {
		lineWidth: number;
		durationMs: number;
		startTimeMs: number;
		nodePhaseStart: number;
		nodePhaseEnd: number;
		shuffleNodes: boolean;
		shuffleSeed: number;
	};
	scribble: {
		durationMs: number;
		startTimeMs: number;
		ease: ScribbleEaseName;
		lineWidth: number;
		blurPx: number;
	};
	letterIntro: {
		style: LetterIntroStyle;
		fadeDurationMs: number;
		blurStartPx: number;
		totalDurationMs: number;
		startDelayMs: number;
		shuffleSeed: number;
	};
	cta: {
		subtitleStartMs: number;
		subtitleFadeMs: number;
		ctaStartMs: number;
		ctaFadeMs: number;
	};
	reducedMotion?: boolean;
};

export function ContentStack({
	mountKey,
	viewportTier,
	hero,
	letterA,
	scribble,
	letterIntro,
	cta,
	reducedMotion = false,
}: ContentStackProps): JSX.Element {
	const { formatMessage } = useIntl();
	const { attemptUserLogin } = useAtlassianUserContext();
	const { fireEvent } = useAnalyticsEvents();
	const handleCtaClick = useCallback((): void => {
		fireEvent('ui.button.clicked.twgMicrositeLogin', { location: 'heroPreviewCta' });
		attemptUserLogin();
	}, [fireEvent, attemptUserLogin]);
	const effectiveSubtitleStart = reducedMotion ? 0 : cta.subtitleStartMs;
	const effectiveCtaStart = reducedMotion ? 0 : cta.ctaStartMs;
	const effectiveSubtitleFadeMs = reducedMotion ? 0 : cta.subtitleFadeMs;
	const effectiveCtaFadeMs = reducedMotion ? 0 : cta.ctaFadeMs;
	const subtitleVisible = useDelayedReveal(effectiveSubtitleStart, mountKey);
	const ctaVisible = useDelayedReveal(effectiveCtaStart, mountKey);
	const contentSpec = CONTENT_SPECS[viewportTier];
	const wordmarkMaxWidth = useMediaQuery(WORDMARK_WIDE_VIEWPORT_QUERY)
		? WORDMARK_MAX_WIDTH.wide
		: WORDMARK_MAX_WIDTH.base;

	return (
		<div
			key={mountKey}
			css={styles.overlay}
			style={{ transform: `translateY(${contentSpec.verticalOffset})` }}
		>
			<div
				css={styles.stack}
				style={{
					maxWidth: wordmarkMaxWidth,
					gap: contentSpec.wordmarkDescriptionGap,
				}}
			>
				<div
					css={styles.wordmarkFrame}
					style={{
						aspectRatio: `${HERO_TYPOGRAPHY_WORDMARK_VIEWBOX.width} / ${HERO_TYPOGRAPHY_WORDMARK_VIEWBOX.height}`,
					}}
				>
					<HeroTypographyWordmark
						accessibleName={formatMessage(messages.heroName)}
						heroScale={hero.scale}
						letterALineWidth={letterA.lineWidth}
						letterADurationMs={letterA.durationMs}
						letterAStartTimeMs={letterA.startTimeMs}
						letterANodePhaseStart={letterA.nodePhaseStart}
						letterANodePhaseEnd={letterA.nodePhaseEnd}
						letterAShuffleNodes={letterA.shuffleNodes}
						letterAShuffleSeed={letterA.shuffleSeed}
						scribbleDurationMs={scribble.durationMs}
						scribbleStartTimeMs={scribble.startTimeMs}
						scribbleEase={scribble.ease}
						scribbleLineWidth={scribble.lineWidth}
						scribbleBlurPx={scribble.blurPx}
						letterIntroTotalDurationMs={letterIntro.totalDurationMs}
						letterIntroStartDelayMs={letterIntro.startDelayMs}
						letterIntroShuffleSeed={letterIntro.shuffleSeed}
						letterIntroStyle={letterIntro.style}
						letterIntroFadeDurationMs={letterIntro.fadeDurationMs}
						letterIntroBlurStartPx={letterIntro.blurStartPx}
						reducedMotion={reducedMotion}
					/>
				</div>
				<div
					css={styles.lowerStack}
					style={{
						gap: contentSpec.descriptionCtaGap,
					}}
				>
					<p
						css={styles.subtitle}
						style={{
							fontSize: contentSpec.descriptionFontSize,
							maxWidth: contentSpec.descriptionMaxWidth,
							opacity: subtitleVisible ? 1 : 0,
							visibility: subtitleVisible ? 'visible' : 'hidden',
							transition: `opacity ${effectiveSubtitleFadeMs}ms ease-out`,
						}}
					>
						<FormattedMessage {...messages.subtitle} />
					</p>
					<span
						css={styles.ctaWrapper}
						style={{
							opacity: ctaVisible ? 1 : 0,
							visibility: ctaVisible ? 'visible' : 'hidden',
							transition: `opacity ${effectiveCtaFadeMs}ms ease-out`,
						}}
					>
						<button
							css={[
								styles.ctaButton,
								fg('platform-dst-motion-uplift-custom-button') && styles.ctaButtonMotion,
							]}
							onClick={handleCtaClick}
						>
							<FormattedMessage {...messages.previewCta} />
						</button>
					</span>
				</div>
			</div>
		</div>
	);
}
